import Head from 'next/head';
import { useTranslation } from 'next-i18next';

import { DEFAULT_LOCALE, Locale, SUPPORTED_LOCALES } from '@/constants/locales';
import { EmailAddresses, reneKrajnc } from '@/constants/rene';
import { Route, Routes } from '@/constants/routes';
import {
  buildLocalisedUrl,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_PATH,
  OG_IMAGE_TYPE,
  OG_IMAGE_WIDTH,
  OG_LOCALE,
  PROFILE_IMAGE_PATH,
} from '@/constants/seo';
import { SITE_URL } from '@/constants/site';
import { SOCIAL_PROFILE_URLS } from '@/constants/social';
import { useSeo } from '@/utils/hooks/useSeo';

interface PageHeadProps {
  route?: Route;
}

const PageHead = ({ route }: PageHeadProps) => {
  const { t, i18n } = useTranslation();
  const { title, description } = useSeo(route || Routes.LANDING_PAGE);

  const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || SITE_URL;
  const routePath = route || Routes.LANDING_PAGE;
  const currentLocale = (i18n.language as Locale) || DEFAULT_LOCALE;
  const pageUrl = buildLocalisedUrl(baseUrl, currentLocale, routePath);
  const imageUrl = `${baseUrl}${OG_IMAGE_PATH}`;
  const profileImageUrl = `${baseUrl}${PROFILE_IMAGE_PATH}`;

  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: reneKrajnc,
    url: baseUrl,
    image: profileImageUrl,
    jobTitle: t('role'),
    email: `mailto:${EmailAddresses.INFO}`,
    sameAs: SOCIAL_PROFILE_URLS,
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: reneKrajnc,
    alternateName: 'renekrajnc.com',
    url: baseUrl,
    inLanguage: SUPPORTED_LOCALES.map((locale) => OG_LOCALE[locale]),
    author: {
      '@type': 'Person',
      name: reneKrajnc,
      url: baseUrl,
    },
  };

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />

      {/* Emit a preload in the SSR HTML so the browser discovers the hero
          portrait before any JS executes, maximising LCP on the landing page. */}
      {route === Routes.LANDING_PAGE && (
        <link
          rel="preload"
          as="image"
          href="/images/rene-anime.png"
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore — fetchPriority is valid HTML but not yet in all TS defs
          fetchPriority="high"
        />
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={reneKrajnc} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:locale" content={OG_LOCALE[currentLocale] ?? OG_LOCALE.en} />
      {SUPPORTED_LOCALES.filter((locale) => locale !== currentLocale).map((locale) => (
        <meta
          key={locale}
          property="og:locale:alternate"
          content={OG_LOCALE[locale] ?? OG_LOCALE.en}
        />
      ))}
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:secure_url" content={imageUrl} />
      <meta property="og:image:type" content={OG_IMAGE_TYPE} />
      <meta property="og:image:width" content={OG_IMAGE_WIDTH} />
      <meta property="og:image:height" content={OG_IMAGE_HEIGHT} />
      <meta property="og:image:alt" content={title} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={title} />

      {SUPPORTED_LOCALES.map((locale) => (
        <link
          key={locale}
          rel="alternate"
          hrefLang={locale}
          href={buildLocalisedUrl(baseUrl, locale, routePath)}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${routePath}`} />

      <link rel="canonical" href={pageUrl} />
    </Head>
  );
};

export default PageHead;
